import { useRouter } from 'expo-router'
import { useCallback, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { Pressable, ScrollView, View, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native'

import { Avatar } from '@/src/elements/avatar'
import { GradientBackground } from '@/src/elements/background'
import { Button } from '@/src/elements/button/default'
import { IMAGE_SIZE } from '@/src/elements/image/image.constants'
import { Icon } from '@/src/elements/media/Icon'
import { Placeholder } from '@/src/elements/placeholder'
import { Typography } from '@/src/elements/text/Typography'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { GENERAL_NOTIFICATION_SETTINGS } from '@/src/features/notification/models/notification.constants'
import { useFavoritePublicationsQuery } from '@/src/features/publication/services/useFavoritePublicationsQuery'
import { DisconnectButton } from '@/src/features/settings/ui/components/DisconnectButton'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { getAvatarUrl } from '@/src/general/utils/getAvatarUrl'
import { getDateWithoutTimezone } from '@/src/general/utils/getDateWithoutTimezone'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { FavoritesSection } from './FavoritesSection'
import { SettingsCard } from './SettingsCard'
import { SettingsCardSettingsContent } from './SettingsCardSettingsContent'
import { SettingsHeader } from './SettingsHeader'
import { SettingsLanguageInput } from './SettingsLanguageInput'
import { SettingsSectionTitle } from './SettingsSectionTitle'

const isCloseToBottom = ({ layoutMeasurement, contentOffset, contentSize }: NativeScrollEvent) => {
  return layoutMeasurement.height + contentOffset.y >= contentSize.height - 600
}

export const SettingsScreen = (): ReactNode => {
  const { t } = useTranslate()
  const { data: me, isError, isLoading, error } = useMeQuery()
  const { data: myFavoritePublications, fetchNextPage, isFetchingNextPage } = useFavoritePublicationsQuery()
  const { lessThan } = useDimensions()
  const { routes } = useRoutes()
  const { navigate } = useRouter()

  const handleScroll = useCallback(
    ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (isCloseToBottom(nativeEvent) && !isFetchingNextPage) fetchNextPage()
    },
    [fetchNextPage, isFetchingNextPage],
  )

  if (isLoading) return <Placeholder testID={TEST_ID.SETTINGS.SCREEN} className="h-80" />
  if (isError || !me)
    return (
      <View testID={TEST_ID.SETTINGS.SCREEN} className="flex-1">
        <ErrorMessage defaultErrorKey="app.error" error={error} />
      </View>
    )

  const { name, email, birthdate, city, avatar } = me
  const utcBirthdate = getDateWithoutTimezone(birthdate)

  return (
    <ScrollView
      testID={TEST_ID.SETTINGS.SCREEN}
      onScroll={handleScroll}
      scrollEventThrottle={160}
      showsVerticalScrollIndicator={!lessThan.md}
      className="bg-primary-bg-medium"
      contentContainerClassName="mx-auto w-full"
    >
      <SettingsHeader />

      <View className="relative">
        <View className="z-20 items-center pt-4">
          <Pressable onPress={() => navigate(routes.settingsAvatars())} testID={TEST_ID.SETTINGS.USER_AVATAR}>
            <Avatar size="2xl" src={getAvatarUrl(avatar, IMAGE_SIZE.AVATAR.BIG)} className="mx-auto" />
          </Pressable>
        </View>

        <View className="relative -mt-16 min-h-[120px] overflow-hidden rounded-t-[140px] pb-32">
          <GradientBackground
            fixed={false}
            svgStyle={{ borderTopLeftRadius: '50% 10rem', borderTopRightRadius: '50% 10rem' }}
          />
          <View className="relative z-10 flex flex-col items-stretch gap-6 max-w-[700px] w-full mx-auto px-4 pb-10 pt-16">
            <View className="flex flex-col items-center gap-1">
              <Typography bold size="xl" black className="text-center">
                {name}
              </Typography>
              <Typography size="sm" contrast="low">
                {email}
              </Typography>
            </View>

            <View className="flex-1 flex-row items-center gap-4 w-full px-4">
              {utcBirthdate && (
                <View className="flex-auto flex-row justify-end items-center gap-1">
                  <Icon name={ICON.BIRTHDAY} size="sm" className="text-primary-icon-contrast-lower ml-auto" />
                  <Typography size="sm" className="text-default-icon-contrast-lower truncate mr-auto">
                    {utcBirthdate.format('D MMMM')}
                  </Typography>
                </View>
              )}
              {!!city && (
                <View className="flex-auto flex-row justify-start items-center gap-2">
                  <Icon name={ICON.MARKER_PIN} size="sm" className="text-success-icon-contrast-lower ml-auto" />
                  <Typography center size="sm" className="text-default-icon-contrast-lower truncate mr-auto">
                    {city}
                  </Typography>
                </View>
              )}
            </View>

            {me?.isPrayerWatchman && (
              <SettingsCard
                icon={ICON.LANTERN}
                secondaryColors
                onPress={() =>
                  navigate(routes.settingsGeneral({ setting: GENERAL_NOTIFICATION_SETTINGS.ON_INTENTION_WATCHMAN }))
                }
              >
                <SettingsCardSettingsContent
                  colors="secondary"
                  title="settings.notifications.watchman.title"
                  description="settings.notifications.watchman.description"
                />
              </SettingsCard>
            )}

            <SettingsLanguageInput />

            {/* EMAILS AND NOTIFICATIONS SECTION */}

            <SettingsSectionTitle>
              <FormattedMessage id="settings.header.notifications" />
            </SettingsSectionTitle>
            <SettingsCard icon={ICON.NOTIFICATION_MESSAGE} onPress={() => navigate(routes.settingsGeneral())}>
              <SettingsCardSettingsContent
                title="settings.notifications.general.title"
                description="settings.notifications.general.description"
              />
            </SettingsCard>
            <SettingsCard icon={ICON.ROAD} onPress={() => navigate(routes.settingsCommunities())}>
              <SettingsCardSettingsContent
                title="settings.notifications.incriptions.title"
                description="settings.notifications.incriptions.description"
              />
            </SettingsCard>

            {/* CREATE CONTENT SECTION */}

            <SettingsSectionTitle>
              <FormattedMessage id="word.create.content" />
            </SettingsSectionTitle>
            <SettingsCard contentClassName="gap-3">
              <Typography>
                <FormattedMessage id="community.creation.suggestion" />
              </Typography>
              <Button
                colors="primary"
                variant="faded"
                onPress={() => navigate(routes.communityCreation())}
                fullWidth={lessThan.xm}
                startIcon={ICON.PLUS}
                testID={TEST_ID.SETTINGS.CREATE_CONTENT_BUTTON}
              >
                {t('word.create.content')}
              </Button>
            </SettingsCard>

            {/* FAVORITE PUBLICATIONS SECTION */}

            <FavoritesSection favorites={myFavoritePublications} />

            <View className="w-full pt-12">
              <DisconnectButton />
            </View>
          </View>
        </View>
      </View>
    </ScrollView>
  )
}
